import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LogOut, User as UserIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Loan Products" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#E5E2DE] bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center" data-testid="navbar-logo-link">
          <BrandLogo size="md" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#5B2C82]" : "text-ink-soft hover:text-[#5B2C82]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"}>
                <Button
                  variant="outline"
                  className="border-[#5B2C82] text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white"
                  data-testid="navbar-dashboard-btn"
                >
                  <UserIcon className="mr-2 h-4 w-4" />
                  {user.role === "admin" ? "Admin" : "My Portal"}
                </Button>
              </Link>
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="navbar-logout-btn"
                className="text-ink-soft hover:text-[#5B2C82]"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" data-testid="navbar-login-btn" className="text-ink-soft hover:text-[#5B2C82]">
                  Sign in
                </Button>
              </Link>
              <Link to="/apply">
                <Button
                  className="bg-[#E87722] text-white hover:bg-[#cc6517]"
                  data-testid="navbar-apply-btn"
                >
                  Apply Now
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="h-6 w-6 text-[#5B2C82]" /> : <Menu className="h-6 w-6 text-[#5B2C82]" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#E5E2DE] bg-white lg:hidden">
          <div className="space-y-1 px-4 py-3">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2 text-base font-medium ${
                    isActive ? "bg-[#F9F6F0] text-[#5B2C82]" : "text-ink-soft"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t border-[#E5E2DE] pt-3">
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/dashboard"}
                    onClick={() => setOpen(false)}
                    className="block rounded-lg px-3 py-2 text-base font-medium text-[#5B2C82]"
                    data-testid="mobile-dashboard-link"
                  >
                    {user.role === "admin" ? "Admin Dashboard" : "My Portal"}
                  </Link>
                  <button
                    onClick={() => {
                      setOpen(false);
                      handleLogout();
                    }}
                    className="block w-full rounded-lg px-3 py-2 text-left text-base font-medium text-ink-soft"
                    data-testid="mobile-logout-btn"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="block rounded-lg px-3 py-2 text-base font-medium text-ink-soft"
                    data-testid="mobile-login-link"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/apply"
                    onClick={() => setOpen(false)}
                    className="mt-1 block rounded-lg bg-[#E87722] px-3 py-2 text-center text-base font-semibold text-white"
                    data-testid="mobile-apply-link"
                  >
                    Apply Now
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
